import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { ApiError } from '../api/client';
import { linkedTargetInRedirect } from '../home/linked-target';
import { useGoogleOneTap } from './google-one-tap';
import { onboardingPathFor, safeRedirect } from './redirect';
import {
  sendMagicLink,
  signInWithGoogle,
  signInWithGoogleIdToken,
} from './service';
import { useAuth } from './use-auth';
import {
  useExtensionAuthHandoff,
  useIsExtensionAuthFlow,
} from './use-extension-auth-handoff';

/**
 * Everything "/login" does, minus how it looks.
 *
 * Sign-in and sign-up are the same act — Firebase has no separate sign-up: a
 * first Google sign-in or magic link registers the account, and the onboarding
 * form finishes it.
 *
 * Ways in: Google — One Tap, or the Firebase popup behind the button — ->
 * refresh the session query ->
 * replace-navigate onward, or a magic link emailed by the API, completed on
 * "/login/verify-link" (see VerifyMagicLinkPage). The redirect travels in
 * the emailed link too, so a different browser still lands on the session.
 */
export function useSignInFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /** Where `ProtectedRoute` was sending them, or home. */
  const redirect = safeRedirect(searchParams.get('redirect'));
  /** The product they came to test, when they came from a floto.ai link. */
  const target = linkedTargetInRedirect(redirect);
  /** The sign-in flow's params, with the redirect as it stands now. */
  const onboardingPath = () => {
    const params = new URLSearchParams(searchParams);
    params.set('redirect', redirect);
    return onboardingPathFor(params);
  };
  const { activated, user, loading, refreshSession } = useAuth();
  const handOffToExtension = useExtensionAuthHandoff();
  /**
   * The extension flow does not leave this page, and that is a correctness
   * requirement rather than a nicety.
   *
   * `chrome.runtime.sendMessage` from a page is only allowed from a URL matching
   * the extension's `externally_connectable.matches` (`<origin>/login*`), and
   * Chrome checks the document's *current* URL. Navigating to `/` while the
   * handoff is still awaiting the allowlist revokes the permission, and the
   * error is Chrome's "Could not establish connection. Receiving end does not
   * exist." — which reads as an uninstalled extension or a dead service worker
   * and cost a day of looking at both.
   *
   * So the tab stays, and the extension closes it on receipt (`onSignedIn` ->
   * `chrome.tabs.remove`). Same shape as the reference implementation, which
   * shows a success state and calls `window.close()`.
   */
  const isExtensionFlow = useIsExtensionAuthFlow();
  const handedOff = useRef(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Resolves an already-authenticated visit before showing the form at all.
   *
   * Without this, the issue-report extension's `/login?source=extension&extId=`
   * handshake only ever fired from inside `handleGoogleSignIn`'s own success
   * path — so a browser that already had a valid Floto session (signed in in
   * another tab, or a previous handshake) showed the sign-in card anyway and
   * needed the user to sign in a second time for no reason. This covers both
   * that case and the plain "/login" visited while already signed in.
   */
  useEffect(() => {
    if (loading || !user || handedOff.current) return;
    handedOff.current = true;
    // Signed in but signup unfinished: the form first. The extension is handed
    // its session from there, once the account can actually be used.
    if (!activated) {
      void navigate(onboardingPath(), { replace: true });
      return;
    }
    // Not awaited — an effect cannot. Which is exactly why the navigation below
    // is conditional: this used to race the handoff and pull the page out from
    // under it.
    void handOffToExtension(user);
    if (!isExtensionFlow) void navigate(redirect, { replace: true });
    // handOffToExtension/navigate are stable-enough refs from their own hooks;
    // this should only re-run when the session itself resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [magicLinkSubmitting, setMagicLinkSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  /** Whether the emailed address already had an account — `null` if the API
   *  could not tell, which the screen words to fit either. */
  const [existingAccount, setExistingAccount] = useState<boolean | null>(null);

  /**
   * Everything after Google says yes, whichever way it said it — the popup
   * behind the button, or a One Tap pick (`signInWithGoogleIdToken`). Both end
   * in the same server session, so they take the same road onward.
   */
  const completeGoogleSignIn = async (
    signIn: () => ReturnType<typeof signInWithGoogle>,
    source: 'google' | 'google_one_tap',
  ) => {
    setError(null);
    setIsSubmitting(true);
    try {
      const session = await signIn();
      // Before the session refresh, so the effect above does not race this.
      handedOff.current = true;
      await refreshSession();
      trackEvent('login_completed', {
        new_user: session.activated === false,
        source,
      });
      // A first sign-in: the API has just registered them, and the onboarding
      // form finishes signup (and does the extension handoff after it).
      if (session.activated === false) {
        void navigate(onboardingPath(), { replace: true });
        return;
      }
      // Awaited: it reads the allowlist before messaging the extension, and the
      // extension is parked waiting for that message.
      // Awaited, and the page must not move until it has been sent.
      if (session.user) await handOffToExtension(session.user);
      if (!isExtensionFlow) void navigate(redirect, { replace: true });
    } catch (err) {
      trackEvent('login_failed', { source });
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // One Tap over the form, for anyone not already on their way in. Off while a
  // sign-in is in flight or a magic link is out, so it never competes with one.
  useGoogleOneTap({
    enabled: !loading && !user && !isSubmitting && !magicLinkSent,
    onCredential: (googleIdToken) => {
      void completeGoogleSignIn(
        () => signInWithGoogleIdToken(googleIdToken),
        'google_one_tap',
      );
    },
  });

  /** What is wrong with the email, shown on the field itself. */
  const [emailError, setEmailError] = useState<string | null>(null);

  const sendEmailLink = async () => {
    setError(null);
    const invalid = emailProblem(email);
    if (invalid) {
      setEmailError(invalid);
      return;
    }
    setEmailError(null);
    setMagicLinkSubmitting(true);
    try {
      const sent = await sendMagicLink(
        email,
        redirect === '/' ? undefined : redirect,
      );
      trackEvent('magic_link_requested', { from_product_link: !!target });
      setExistingAccount(sent.existingAccount);
      setMagicLinkSent(true);
    } catch (err) {
      trackEvent('login_failed', { source: 'magic_link_request' });
      // On the field, like a typo would be: it is the email flow that failed.
      setEmailError(magicLinkError(err));
    } finally {
      setMagicLinkSubmitting(false);
    }
  };

  return {
    email,
    emailError,
    error,
    existingAccount,
    isExtensionFlow,
    isSubmitting,
    magicLinkSent,
    magicLinkSubmitting,
    onEmailChange: (next: string) => {
      setEmail(next);
      // Typing again is answering the error; don't keep shouting it.
      setEmailError(null);
    },
    onSendMagicLink: () => {
      void sendEmailLink();
    },
    onSignIn: () => {
      void completeGoogleSignIn(signInWithGoogle, 'google');
    },
    /**
     * Already signed in. For an ordinary visit the effect above is navigating
     * away, so the screen renders nothing rather than flashing the form. For
     * the extension flow there is nowhere to go — the tab exists only to hand
     * the session over, and the extension closes it.
     */
    signedIn: !loading && Boolean(user) && Boolean(activated),
    target,
  };
}

/**
 * What to say when a sign-in link could not be sent.
 *
 * A 4xx is the API refusing for a reason it states for a person — "Please
 * wait 42s before requesting another link." for the resend cooldown — so that
 * is what they see, without the client's " (400)" suffix. Anything else
 * (network, a 5xx) gets the generic line; its message is not written for them.
 */
export function magicLinkError(err: unknown): string {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    const message = err.message.replace(/\s*\(\d{3}\)$/, '').trim();
    if (message) return message;
  }
  return 'Could not send a sign-in link. Please try again shortly.';
}

/**
 * Why `email` can't be sent to yet, or null when it can.
 *
 * Stricter than `<input type="email">`, which passes `pooja@floto` because a
 * bare host is a valid address on paper — but it is never one anybody signing
 * up here means, and letting it through only gets a vaguer refusal from the
 * API a round trip later.
 */
export function emailProblem(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return 'Enter your email to get a sign-in link.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(trimmed)) {
    return 'That email looks incomplete. Try name@company.com.';
  }
  return null;
}
